import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiCheck, FiMail, FiMapPin, FiUser, FiTag } = FiIcons;

const SignUpModal = ({ isOpen, onClose, selectedPlan = null }) => {
  const { signUp, dbOperations } = useAuth();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: City Information
    cityName: '',
    email: '',
    contactPerson: '',
    phoneNumber: '',
    
    // Step 2: Plan Selection & Payment (no password needed - magic link only)
    selectedTier: selectedPlan || 'professional',
    couponCode: '',
    agreeToTerms: false,
    
    // Additional info
    country: '',
    website: '',
    expectedVisitors: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponResult, setCouponResult] = useState(null);

  // Updated plans - only 2 tiers with all technical features
  const plans = [
    {
      id: 'professional',
      name: 'Professional', 
      price: 129,
      period: 'pro Monat',
      features: [
        'Unbegrenzte Selfies',
        'Vollständige Anpassung',
        'Analytics Dashboard',
        'QR-Code Generator',
        'Custom Domains',
        'White-Label Option',
        'API Zugang',
        'Multi-Location Support',
        'Email Support'
      ],
      popular: true
    },
    {
      id: 'concierge',
      name: 'Concierge',
      price: 229,
      period: 'pro Monat',
      features: [
        'Alle Professional Features',
        '✨ All done for you Service', // Unique to Concierge
        '🚀 Priority Support', // Unique to Concierge
        'Dedicated Account Manager',
        'Setup-Service inklusive',
        'Custom Entwicklung',
        'Persönlicher Ansprechpartner',
        '24/7 Premium Support',
        'Concierge Service'
      ]
    }
  ];

  const validateStep = (stepNumber) => {
    const newErrors = {};

    if (stepNumber === 1) {
      if (!formData.cityName.trim()) {
        newErrors.cityName = 'Stadt-Name ist erforderlich';
      }
      if (!formData.email.trim()) {
        newErrors.email = 'E-Mail ist erforderlich';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'E-Mail Format ist ungültig';
      }
      if (!formData.contactPerson.trim()) {
        newErrors.contactPerson = 'Ansprechpartner ist erforderlich';
      }
    }

    if (stepNumber === 2) {
      if (!formData.agreeToTerms) {
        newErrors.agreeToTerms = 'Sie müssen den Nutzungsbedingungen zustimmen';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
    setErrors({});
  };

  const applyCoupon = async () => {
    if (!formData.couponCode.trim()) {
      setErrors({ ...errors, couponCode: 'Bitte geben Sie einen Gutscheincode ein' });
      return;
    }

    try {
      const selectedPlan = plans.find(p => p.id === formData.selectedTier);
      
      const result = await dbOperations.calculateCouponDiscount(
        formData.couponCode,
        selectedPlan.price
      );

      if (result.valid) {
        setCouponApplied(true);
        setCouponResult(result);
        setErrors({ ...errors, couponCode: '' });
      } else {
        setErrors({ ...errors, couponCode: result.message || 'Ungültiger Gutscheincode' });
        setCouponApplied(false);
        setCouponResult(null);
      }
    } catch (error) {
      console.error('Error applying coupon:', error);
      setErrors({ ...errors, couponCode: 'Fehler beim Prüfen des Gutscheincodes' });
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) return;

    setIsLoading(true);
    try {
      const selectedPlan = plans.find(p => p.id === formData.selectedTier);
      const finalPrice = couponResult ? couponResult.final_price : selectedPlan.price;
      const discountAmount = couponResult ? couponResult.discount_amount : 0;
      
      const signUpData = {
        ...formData,
        plan: selectedPlan,
        finalPrice,
        couponApplied: couponApplied ? formData.couponCode : null,
        discount: discountAmount
      };

      const result = await signUp(signUpData);
      
      if (result.success) {
        // Close modal and show success message or redirect
        onClose();
        // The AuthContext will handle the next steps (email verification -> onboarding -> dashboard)
      } else {
        setErrors({ submit: result.error || 'Registrierung fehlgeschlagen' });
      }
    } catch (error) {
      setErrors({ submit: 'Ein unerwarteter Fehler ist aufgetreten' });
    } finally {
      setIsLoading(false);
    }
  };

  const getCalculatedPrice = () => {
    if (couponResult) {
      return couponResult.final_price;
    }
    const selectedPlan = plans.find(p => p.id === formData.selectedTier);
    return selectedPlan.price;
  };

  const getDiscountAmount = () => {
    return couponResult ? couponResult.discount_amount : 0;
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {step === 1 && 'Stadt-Informationen'}
              {step === 2 && 'Plan auswählen'}
            </h2>
            <p className="text-gray-600 text-sm">
              Schritt {step} von 2
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            {[1, 2].map((num) => (
              <div
                key={num}
                className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${
                  num <= step
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {num < step ? <SafeIcon icon={FiCheck} /> : num}
              </div>
            ))}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(step / 2) * 100}%` }}
            />
          </div>
        </div>

        {/* Step 1: City Information */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-xl mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">🔐 Passwortloser Zugang</h3>
              <p className="text-blue-700 text-sm">
                Sie erhalten einen sicheren Login-Link per E-Mail. Kein Passwort erforderlich!
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiMapPin} className="inline mr-2" />
                  Stadt-Name *
                </label>
                <input
                  type="text"
                  value={formData.cityName}
                  onChange={(e) => setFormData({ ...formData, cityName: e.target.value })}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.cityName ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="z.B. München"
                />
                {errors.cityName && (
                  <p className="text-red-600 text-sm mt-1">{errors.cityName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiMail} className="inline mr-2" />
                  E-Mail Adresse *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="kontakt@stadt.de"
                />
                {errors.email && (
                  <p className="text-red-600 text-sm mt-1">{errors.email}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiUser} className="inline mr-2" />
                  Ansprechpartner *
                </label>
                <input
                  type="text"
                  value={formData.contactPerson}
                  onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.contactPerson ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Max Mustermann"
                />
                {errors.contactPerson && (
                  <p className="text-red-600 text-sm mt-1">{errors.contactPerson}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefonnummer
                </label>
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="+49 123 456789"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Land
                </label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Deutschland"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://www.stadt.de"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Erwartete Besucher pro Monat
              </label>
              <select
                value={formData.expectedVisitors}
                onChange={(e) => setFormData({ ...formData, expectedVisitors: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Bitte auswählen</option>
                <option value="under-1000">Unter 1.000</option>
                <option value="1000-5000">1.000 - 5.000</option>
                <option value="5000-10000">5.000 - 10.000</option>
                <option value="10000-50000">10.000 - 50.000</option>
                <option value="over-50000">Über 50.000</option>
              </select>
            </div>
          </div>
        )}

        {/* Step 2: Plan Selection */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className={`relative p-6 border-2 rounded-xl cursor-pointer transition-all ${
                    formData.selectedTier === plan.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => {
                    setFormData({ ...formData, selectedTier: plan.id });
                    // Reset coupon when plan changes
                    setCouponApplied(false);
                    setCouponResult(null);
                  }}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                        Beliebt
                      </span>
                    </div>
                  )}
                  
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          formData.selectedTier === plan.id
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-300'
                        }`}>
                          {formData.selectedTier === plan.id && (
                            <div className="w-2 h-2 bg-white rounded-full" />
                          )}
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">{plan.name}</h3>
                          <p className="text-3xl font-bold text-gray-900 mt-1">
                            {plan.price}€
                            <span className="text-lg font-normal text-gray-600">/{plan.period}</span>
                          </p>
                          {plan.id === 'concierge' && (
                            <p className="text-sm text-blue-600 mt-1 font-medium">
                              ✨ Wir machen alles für Sie
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {plan.features.map((feature, index) => (
                          <div key={index} className="flex items-center text-sm text-gray-700">
                            <SafeIcon icon={FiCheck} className="text-green-500 mr-2 flex-shrink-0" />
                            <span className={
                              (feature.includes('All done for you') || feature.includes('Priority Support')) 
                                ? 'font-semibold text-blue-700' 
                                : ''
                            }>
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Coupon Code */}
            <div className="bg-gray-50 p-4 rounded-xl">
              <div className="flex items-center gap-3 mb-3">
                <SafeIcon icon={FiTag} className="text-gray-600" />
                <h3 className="font-semibold text-gray-900">Gutscheincode</h3>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.couponCode}
                  onChange={(e) => setFormData({ ...formData, couponCode: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="z.B. TESTFREE"
                />
                <button
                  type="button"
                  onClick={applyCoupon}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Einlösen
                </button>
              </div>
              {errors.couponCode && (
                <p className="text-red-600 text-sm mt-1">{errors.couponCode}</p>
              )}
              {couponApplied && couponResult && (
                <p className="text-green-600 text-sm mt-1 flex items-center">
                  <SafeIcon icon={FiCheck} className="mr-1" />
                  Gutschein angewendet: -{getDiscountAmount()}€
                </p>
              )}
              
              {/* Available coupons hint */}
              <div className="mt-2 text-xs text-gray-500">
                <p>Verfügbare Codes: TESTFREE (100% kostenlos), WELCOME50 (50%), LAUNCH (75%)</p>
              </div>
            </div>

            {/* Price Summary */}
            <div className="bg-blue-50 p-4 rounded-xl">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-700">Gewählter Plan:</span>
                <span className="font-semibold">
                  {plans.find(p => p.id === formData.selectedTier)?.name}
                </span>
              </div>
              {couponApplied && getDiscountAmount() > 0 && (
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-700">Rabatt:</span>
                  <span className="text-green-600 font-semibold">-{getDiscountAmount()}€</span>
                </div>
              )}
              <div className="border-t pt-2 flex justify-between items-center">
                <span className="text-lg font-semibold">Gesamtpreis:</span>
                <span className="text-2xl font-bold text-blue-600">
                  {getCalculatedPrice()}€/Monat
                </span>
              </div>
              {getCalculatedPrice() === 0 && (
                <p className="text-green-600 text-sm mt-1 text-center font-semibold">
                  🎉 Kostenlos dank Gutscheincode!
                </p>
              )}
            </div>

            {/* Terms and Conditions */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={(e) => setFormData({ ...formData, agreeToTerms: e.target.checked })}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="agreeToTerms" className="text-sm text-gray-700">
                Ich stimme den{' '}
                <a href="#" className="text-blue-600 hover:underline">
                  Nutzungsbedingungen
                </a>{' '}
                und der{' '}
                <a href="#" className="text-blue-600 hover:underline">
                  Datenschutzerklärung
                </a>{' '}
                zu. *
              </label>
            </div>
            {errors.agreeToTerms && (
              <p className="text-red-600 text-sm">{errors.agreeToTerms}</p>
            )}

            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-700 text-sm">{errors.submit}</p>
              </div>
            )}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t">
          <button
            onClick={step === 1 ? onClose : handleBack}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
          >
            {step === 1 ? 'Abbrechen' : 'Zurück'}
          </button>

          {step < 2 ? (
            <button
              onClick={handleNext}
              className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
            >
              Weiter
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-70"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Wird erstellt...
                </>
              ) : (
                <>
                  <SafeIcon icon={FiCheck} />
                  Account erstellen
                </>
              )}
            </button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SignUpModal;